import baseApi from "../api/baseApi";

const categoriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createCategory: builder.mutation({
      query: (payload) => ({
        url: "/category/",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["Categories"],
    }),

    getAllCategories: builder.query({
      query: () => {
        return {
          url: "/category/",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Categories"],
    }),

    // Get all categories - Admin
    getAllCategoriesAdmin: builder.query({
      query: ({ page = 1, limit = 10, search = "", status = "" } = {}) => {
        const params = {
          page: String(page),
          limit: String(limit),
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        if (status && status !== "all") {
          params.status = status;
        }

        return {
          url: "/category/admin",
          method: "GET",
          credentials: "include",
          params,
        };
      },
      providesTags: ["Categories"],
    }),

    // Get single category - Admin
    getSingleCategoryAdmin: builder.query({
      query: (categoryId) => ({
        url: `/category/admin/${categoryId}`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (result, error, categoryId) => [
        { type: "Categories", id: categoryId },
      ],
    }),
    updateCategory: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/category/${id}`,
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["Categories"],
    }),

    deleteCategory: builder.mutation({
      query: (categoryId) => ({
        url: `/category/${categoryId}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["Categories"],
    }),

    deleteCategoryPermanently: builder.mutation({
      query: (categoryId) => ({
        url: `/category/${categoryId}/permanent`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["Categories"],
    }),

    getAllSubCategories: builder.query({
      query: () => {
        return {
          url: "/category/subcategories/",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["SubCategories"],
    }),

    // Create subcategory
    createSubCategory: builder.mutation({
      query: (payload) => ({
        url: "/category/subcategories/",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["SubCategories", "Categories"],
    }),

    // Update subcategory
    updateSubCategory: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/category/subcategories/${id}`,
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["SubCategories", "Categories"],
    }),
    getAllSubCategoriesAdmin: builder.query({
      query: () => {
        return {
          url: "/category/subcategories/admin",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["SubCategories"],
    }),

    getAllAgeGroups: builder.query({
      query: () => ({
        url: "/category/agegroups/",
        method: "GET",
        credentials: "include",
      }),
      providesTags: ["AgeGroups"],
    }),

    // Create age group
    createAgeGroup: builder.mutation({
      query: (payload) => ({
        url: "/category/agegroups/",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["AgeGroups"],
    }),

    // Update age group
    updateAgeGroup: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/category/agegroups/${id}`,
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["AgeGroups"],
    }),
    // Get all age groups for admin
    getAllAgeGroupsAdmin: builder.query({
      query: () => ({
        url: "/category/agegroups/admin",
        method: "GET",
        credentials: "include",
      }),
      providesTags: ["AgeGroups"],
    }),
    // Get all levels
    getAllLevels: builder.query({
      query: () => ({
        url: "/category/levels/",
        method: "GET",
        credentials: "include",
      }),
      providesTags: ["Levels"],
    }),

    // Create level
    createLevel: builder.mutation({
      query: (payload) => ({
        url: "/category/levels/",
        method: "POST",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["Levels"],
    }),

    // Update level
    updateLevel: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/category/levels/${id}`,
        method: "PATCH",
        credentials: "include",
        body: payload,
      }),
      invalidatesTags: ["Levels"],
    }),

    // Get all levels for admin
    getAllLevelsAdmin: builder.query({
      query: () => ({
        url: "/category/levels/admin",
        method: "GET",
        credentials: "include",
      }),
      providesTags: ["Levels"],
    }),
  }),
});

export const {
  useCreateCategoryMutation,
  useGetAllCategoriesQuery,
  useGetAllCategoriesAdminQuery,
  useGetSingleCategoryAdminQuery,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useDeleteCategoryPermanentlyMutation,
  useGetAllSubCategoriesQuery,
  useCreateSubCategoryMutation,
  useUpdateSubCategoryMutation,
  useGetAllSubCategoriesAdminQuery,
  useGetAllAgeGroupsQuery,
  useCreateAgeGroupMutation,
  useUpdateAgeGroupMutation,
  useGetAllAgeGroupsAdminQuery,
  useGetAllLevelsQuery,
  useCreateLevelMutation,
  useUpdateLevelMutation,
  useGetAllLevelsAdminQuery,
} = categoriesApi;

export default categoriesApi;
